import { ChartTypes } from '@mode/shared/contract-common';
import { getDefaultGeneralFormat } from '../table-helpers/table-format-helpers';

export const DEFAULT_CHART_TITLE = 'Chart';

export const DEFAULT_CHART_FORMAT: Readonly<ChartTypes.ChartFormat> = {
  xAxis: {
    rotate: null,
    dateFormat: 'auto',
    numberRange: { min: null, max: null },
    numberFormat: { format: null, precision: null },
    title: null,
    titleEnabled: true,
    compact: false,
    showAxisLabels: true,
    axisLabels: null,
    baseFormat: {},
  },
  yAxis: {
    numberRange: { min: null, max: null },
    numberFormat: { format: null, precision: null },
    title: null,
    titleEnabled: true,
    scaleType: 'linear',
    baseFormat: {},
    valueFieldFormatMap: {},
  },
  y2Axis: {
    numberRange: { min: null, max: null },
    numberFormat: { format: null, precision: null },
    title: null,
    titleEnabled: true,
    scaleType: 'linear',
    baseFormat: {},
  },
  flatTable: {
    general: getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.FlatTableViz),
    columns: [],
  },
  legend: {
    enabled: true,
    position: 'auto',
    title: null,
    titleEnabled: true,
    baseFormat: {},
  },
  bigValue: {
    description: null,
    primaryTrendFormat: {
      label: null,
    },
  },
  nullToZero: false,
  showValues: true,
  pieLabelsNotPercentages: false,
  colors: {
    theme: 'contrast',
    custom: [],
    map: {},
  },
  drilldownEnabled: true,
  useMuzeRenderer: false,
};

export const CHART_TYPE_ICONS: Record<ChartTypes.ChartType, string> = {
  bar: 'icon-chart-grouped-bar-vert',
  hBar: 'icon-chart-grouped-bar-horiz',
  stackedBar: 'icon-chart-bar-vert',
  stackedBar100: 'icon-chart-bar-100-vert',
  hStackedBar: 'icon-chart-bar-horiz',
  hStackedBar100: 'icon-chart-bar-100-horiz',
  bigValue: 'icon-chart-big-value',
  pivotTable: 'icon-chart-pivot',
  linePlusBar: 'icon-chart-line-bar',
  line: 'icon-chart-line',
  area: 'icon-chart-area',
  totalArea: 'icon-chart-area-100',
  scatter: 'icon-chart-scatterplot',
  pie: 'icon-chart-pie',
  donut: 'icon-chart-donut',
  table: 'icon-chart-table',
  vegas: 'icon-chart-explorer',
  vegasPivotTable: 'icon-chart-pivot',
  histogram: 'icon-chart-histogram',
};

export const LEGACY_CHART_TYPE_ICONS: Record<ChartTypes.V1ChartType, string> = {
  bar: 'icon-chart-grouped-bar-vert',
  stackedBar: 'icon-chart-bar-vert',
  bigNumber: 'icon-chart-big-number-deprecated',
  pivotTable: 'icon-chart-display-table-deprecated',
  linePlusBar: 'icon-chart-line-bar',
  line: 'icon-chart-line',
  area: 'icon-chart-area',
  scatter: 'icon-chart-scatterplot',
  pie: 'icon-chart-pie',
};

export const CHART_CONVERSION_TABLE: Record<ChartTypes.ChartType, string> = {
  stackedBar: 'Bar',
  hBar: 'Grouped Bar',
  bar: 'Grouped Bar',
  hStackedBar: 'Bar',
  stackedBar100: '100% Bar',
  hStackedBar100: '100% Bar',
  pie: 'Pie',
  donut: 'Donut',
  bigValue: 'Big Value',
  pivotTable: 'Pivot Table',
  linePlusBar: 'Line + Bar',
  totalArea: 'Area',
  table: 'Table',
  vegas: 'Visual Explorer',
  line: 'Line',
  area: 'Area',
  scatter: 'Scatter',
  vegasPivotTable: 'Pivot Table',
  histogram: 'Histogram',
};

export const LEGACY_CHART_CONVERSION_TABLE: Record<ChartTypes.V1ChartType, string> = {
  bar: 'Grouped Bar',
  stackedBar: 'Bar',
  bigNumber: 'Big Number',
  pivotTable: 'Pivot Table',
  linePlusBar: 'Line + Bar',
  line: 'Line',
  area: 'Area',
  scatter: 'Scatter',
  pie: 'Pie',
};

export function getChartIcon(type: ChartTypes.ChartType): string {
  return type ? CHART_TYPE_ICONS[type] : CHART_TYPE_ICONS['bar'];
}

export function getLegacyChartIcon(type: ChartTypes.V1ChartType): string {
  return type ? LEGACY_CHART_TYPE_ICONS[type] : LEGACY_CHART_TYPE_ICONS['bar'];
}

export function getChartTitle(chart: ChartTypes.ChartResource) {
  return chart.view_vegas.title;
}

export function getChartSuffix(type: ChartTypes.ChartType): string {
  if (['pivotTable', 'table', 'vegas', 'histogram'].includes(type)) {
    return '';
  }

  return DEFAULT_CHART_TITLE;
}

export function getDisplayChartType(type: ChartTypes.ChartType): string | null {
  const result = CHART_CONVERSION_TABLE[type];

  if (result) {
    return result[0].toUpperCase() + result.slice(1);
  }

  return null;
}

export function getChartName(name: string | undefined, type: ChartTypes.ChartType): string {
  return name || [getDisplayChartType(type), getChartSuffix(type)].filter((v) => v).join(' ');
}

// Temporary function while we support legacy chart types
export function getLegacyChartName(name: string | undefined, type: ChartTypes.V1ChartType): string {
  const legacyChartType = LEGACY_CHART_CONVERSION_TABLE[type];
  const displayChartType = legacyChartType
    ? `Legacy ${legacyChartType[0].toUpperCase()}${legacyChartType.slice(1)}`
    : null;
  const chartSuffix = ['pivotTable', 'table', 'vegas'].includes(type) ? '' : DEFAULT_CHART_TITLE;
  return name || [displayChartType, chartSuffix].filter((v) => v).join(' ');
}
